/**
 * ProductAuditListPresenter
 * --
 */
import React, { useState, useMemo } from 'react';
import { Content, Title, ModalLayout } from 'components';
import {
  Select,
  Table,
  Input,
  Button,
  Tag,
  Spin,
  Divider,
  Statistic,
  Space,
  Flex,
} from 'antd';
import { stringToMoneyFormat } from 'utils';
import {
  PRODUCT_TYPE,
  PRODUCT_STATUS,
  APPLICATION_STATUS,
} from 'constants/ProductConstants';
import {
  EditOutlined,
  SearchOutlined,
  BellOutlined,
  MessageOutlined,
} from '@ant-design/icons';
import ProductDetail from 'routes/admin.routes/admin.products/ProductDetail';
import { useUserStack } from 'store';
import { downloadXlsx } from 'utils/XlsxDownloader';
import Formatter from 'utils/Formatter';

/**----------------------------------------
 * [Component] 입점업체 상세: 프레젠터
 * ----------------------------------------
 */
const ProductAuditListPresenter = ({
  loading,
  productList,
  permission,
  onChangeProduct,
  searchValue,
  onChange,
  onSendNotification = () => {},
}) => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  const userInfo = useUserStack();

  /* ===== STATE ===== */
  const [productFeedback, setProductFeedback] = useState('');
  const [productFeedbackModal, setProductFeedbackModal] = useState(null);
  const [productModal, setProductModal] = useState(null);
  /* ===== VARIABLE ===== */
  /* ===== FUNCTION ===== */
  /* ===== HOOKS ===== */
  /* ===== RENDER ===== */
  // console.log('shopInfomation: ', shopInfomation);
  // console.log('productList: ', productList);
  const filteredProducts = useMemo(() => {
    let arr = productList;
    const { product_name, shop_information_name } = searchValue;
    if (product_name) {
      arr = arr?.filter((product) =>
        product?.product_name
          .toLowerCase()
          ?.includes(product_name.toLowerCase())
      );
    }

    if (shop_information_name) {
      arr = arr?.filter((product) =>
        product?.shop_information?.shop_information_name?.includes(
          shop_information_name.toLowerCase()
        )
      );
    }

    return arr;
  }, [productList, searchValue]);
  const col = [
    {
      title: '상품코드',
      dataIndex: 'product_code',
      width: 150,
      align: 'center',
    },

    // 총관리자일때만 판매업체 보이기
    ...(permission === 'ADMIN'
      ? [
          {
            title: '판매업체',
            dataIndex: 'shop_information',
            width: 240,
            align: 'center',
            render: (value) => value?.shop_information_name,
            filterDropdown: ({ selectedKeys, confirm, clearFilters }) => (
              <div style={{ padding: 8 }}>
                <Input
                  value={searchValue?.shop_information_name}
                  onChange={(e) =>
                    onChange('shop_information_name', e.target.value)
                  }
                  onPressEnter={() => confirm()}
                  placeholder={`상품명 검색`}
                />
                <Space style={{ marginTop: 8 }}>
                  <Button
                    type="primary"
                    onClick={() => confirm()}
                    icon={<SearchOutlined />}
                    size="small"
                    style={{ width: 90 }}
                  >
                    검색
                  </Button>
                  <Button
                    onClick={() => {
                      onChange('shop_information_name', null); // 초기화 시 검색 텍스트 비우기
                      clearFilters();
                    }}
                    size="small"
                    style={{ width: 90 }}
                  >
                    초기화
                  </Button>
                </Space>
              </div>
            ),
          },
        ]
      : []),
    {
      title: '상품명',
      dataIndex: 'product_name',
      width: 300,
      align: 'center',
      render: (v, record) => (
        <a
          href={`/#/products/${record.product_code}`}
          style={{ textDecoration: 'underline' }}
          onClick={(e) => {
            e.preventDefault();
            setProductModal(record);
          }}
        >
          {v}
        </a>
      ),
      filterDropdown: ({ selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            value={searchValue?.product_name}
            onChange={(e) => onChange('product_name', e.target.value)}
            onPressEnter={() => confirm()}
            placeholder={`상품명 검색`}
          />
          <Space style={{ marginTop: 8 }}>
            <Button
              type="primary"
              onClick={() => confirm()}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              검색
            </Button>
            <Button
              onClick={() => {
                onChange('product_name', null); // 초기화 시 검색 텍스트 비우기
                clearFilters();
              }}
              size="small"
              style={{ width: 90 }}
            >
              초기화
            </Button>
          </Space>
        </div>
      ),
    },

    {
      title: '상품유형',
      dataIndex: 'type',
      width: 100,
      align: 'center',
      render: (v) => PRODUCT_TYPE[v].title,
      filters: [
        { text: '배송상품', value: 'DELIVERY' },
        { text: '대여상품', value: 'RENTAL' },
        { text: '구독상품', value: 'SUBSCRIPTION' },
      ],
      onFilter: (value, record) => {
        return record?.type?.includes(value);
      },
    },
    {
      title: '금액',
      dataIndex: 'price',
      width: 100,
      align: 'center',
      render: (v) => (
        <p style={{ textAlign: 'center' }}>{stringToMoneyFormat(v)}</p>
      ),
      sorter: (a, b) => a.price - b.price,
    },
    {
      title: '옵션',
      dataIndex: 'product_option',
      width: 100,
      align: 'center',
      render: (v) => `${v?.length ?? 0}개`,
    },

    {
      title: '노출여부',
      dataIndex: 'is_show',
      width: 90,
      align: 'center',
      render: (v) => (v === 'Y' ? '노출' : '미노출'),
      filters: [
        { text: '노출', value: 'Y' },
        { text: '미노출', value: 'N' },
      ],
      onFilter: (value, record) => {
        return record?.is_show === value;
      },
    },
    {
      title: '상태',
      dataIndex: 'product_status',
      width: 90,
      align: 'center',
      render: (v) => {
        const { color, title } = PRODUCT_STATUS[v];
        return <Tag color={color}>{title}</Tag>;
      },
      filters: [
        { text: '품절', value: 'SOLDOUT' },
        { text: '정상', value: 'NORMAL' },
        { text: '상품종료', value: 'END' },
        { text: '중지', value: 'STOP' },
        { text: '대기', value: 'WAIT' },
      ],
      onFilter: (value, record) => {
        return record?.product_status === value;
      },
    },
    {
      title: '판매상태',
      width: 115,
      align: 'product_status',
      render: (v) => {
        return (
          <>
            <Select
              disabled={permission !== 'ADMIN'}
              value={v.product_status}
              style={{
                width: '100%',
              }}
              options={Object.keys(PRODUCT_STATUS).map((value) => ({
                label: PRODUCT_STATUS[value]?.title,
                value,
              }))}
              onChange={(value) =>
                onChangeProduct(v.product_id, 'product_status', value)
              }
            />
          </>
        );
      },
    },
    {
      title: '등록신청',
      dataIndex: 'application_status',
      width: 90,
      align: 'center',
      render: (v) => {
        const { color, title } = APPLICATION_STATUS[v];
        return <Tag color={color}>{title}</Tag>;
      },
      filters: [
        { text: '신청', value: 'APPLY' },
        { text: '반려', value: 'REJECT' },
        { text: '승인', value: 'APPROVE' },
        { text: '대기', value: 'WAIT' },
      ],
      onFilter: (value, record) => {
        return record?.application_status === value;
      },
    },
    {
      title: '등록신청 심사',
      width: 115,
      align: 'center',
      render: (v) => {
        return (
          <>
            <Select
              disabled={permission !== 'ADMIN'}
              value={v.application_status}
              style={{
                width: '100%',
              }}
              options={Object.keys(APPLICATION_STATUS).map((value) => ({
                label: APPLICATION_STATUS[value].title,
                value,
              }))}
              onChange={(value) =>
                onChangeProduct(v.product_id, 'application_status', value)
              }
            />
          </>
        );
      },
    },

    {
      title: '등록일',
      dataIndex: 'created_at',
      width: 90,
      align: 'center',
      render: (v) => Formatter.unixToDate(v, 'YYYY.MM.DD'),
      sorter: (a, b) => a.created_at - b.created_at,
    },
    {
      title: '수정일',
      dataIndex: 'updated_at',
      width: 90,
      align: 'center',
      render: (v) => Formatter.unixToDate(v, 'YYYY.MM.DD'),
      sorter: (a, b) => a.updated_at - b.updated_at,
    },

    // 총관리자일때만 판매업체 보이기
    ...(permission === 'ADMIN'
      ? [
          {
            title: '피드백',
            width: 120,
            align: 'center',
            render: (v) => (
              <>
                <Button
                  color="primary"
                  variant="solid"
                  onClick={() => setProductFeedbackModal(v)}
                >
                  <EditOutlined /> 등록
                </Button>
              </>
            ),
          },
          {
            title: '재입고알림',
            width: 120,
            align: 'center',
            fixed: 'right',
            render: (v) => (
              <Flex gap={5}>
                <Button onClick={() => onSendNotification('PUSH', v)}>
                  <BellOutlined style={{ color: '#EE4700' }} />
                </Button>
                <Button onClick={() => onSendNotification('SMS', v)}>
                  <MessageOutlined />
                </Button>
              </Flex>
            ),
          },
        ]
      : [
          {
            title: '피드백',
            width: 120,
            align: 'center',
            render: (v) => (
              <>
                <Button
                  disabled={!v.apply_feedback}
                  color="primary"
                  variant="solid"
                  onClick={() => setProductFeedbackModal(v)}
                >
                  <EditOutlined /> 보기
                </Button>
              </>
            ),
          },
        ]),
  ];

  return (
    <>
      <Content maxWidth={'100%'} padding={20} backgroundColor={'none'}>
        <Spin spinning={loading}>
          <Title size={4} style={{ marginBottom: 10 }}>
            상품심사 목록
          </Title>

          {/* === 현황 요약 === */}
          <Content
            fullWidth
            padding={0}
            backgroundColor={'none'}
            style={{
              marginBottom: 4,
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            {[
              {
                title: '총 등록상품 수',
                value: stringToMoneyFormat(productList.length),
                suffix: '건',
              },
              {
                title: '노출상품',
                value: stringToMoneyFormat(
                  productList.filter((item) => item.is_show === 'Y').length
                ),
                suffix: '개',
              },
              {
                title: '심사신청 상품',
                value: stringToMoneyFormat(
                  productList.filter(
                    (item) => item.application_status === 'APPLY'
                  ).length
                ),
                suffix: '개',
              },
              {
                title: '승인된 상품',
                value: stringToMoneyFormat(
                  productList.filter(
                    (item) => item.application_status === 'APPROVE'
                  ).length
                ),
                suffix: '개',
              },
              {
                title: '반려된 상품',
                value: stringToMoneyFormat(
                  productList.filter(
                    (item) => item.application_status === 'REJECT'
                  ).length
                ),
                suffix: '개',
              },
            ].map((_item, _index) => (
              <div
                key={`${_item.label}-${_index}`}
                className="boxShadow"
                style={{
                  background: '#FFFFFF',
                  borderRadius: 4,
                  width: `90%`,
                  margin: '0 2px',
                  padding: '24px 15px',
                }}
              >
                <Statistic
                  // className="boxShadow"
                  direction={'row'}
                  size="small"
                  title={_item.title}
                  value={_item.value}
                  suffix={_item.suffix}
                  style={{ justifyContent: 'center' }}
                />
              </div>
            ))}
          </Content>
          {/* === 상품목록 === */}
          <Content
            maxWidth={'100%'}
            padding={'10px 15px'}
            backgroundColor={'#fff'}
          >
            <Title
              size={5}
              style={{ marginBottom: 10 }}
              subButton={
                <Button
                  onClick={() =>
                    downloadXlsx({
                      columns: [
                        ...col
                          ?.map((_item) => {
                            if (!_item?.dataIndex && !_item?.key) return null;
                            return _item;
                          })
                          ?.filter((_item) => _item !== null),
                        {
                          title: '피드백',
                          key: 'apply_feedback',
                        },
                      ],

                      rows: filteredProducts?.map((_item) => {
                        return {
                          ..._item,
                          product_option: _item?.product_option?.length ?? 0,
                        };
                      }),
                      fileName: `입점업체_상품목록`,
                    })
                  }
                >
                  엑셀로 다운로드
                </Button>
              }
            >
              등록상품 ({filteredProducts.length})
            </Title>
            <div>
              <Table
                bordered
                size="small"
                // style={{ minWidth: 1500, width: '100%' }}
                scroll={{ x: 1500, y: 800 }}
                dataSource={filteredProducts ?? []}
                columns={col}
              />
            </div>
          </Content>

          {/* ===== 피드백 등록 모달 ===== */}
          {permission === 'ADMIN' ? (
            <>
              <ModalLayout
                width={400}
                maxWidth={'calc(100% - 250px)'}
                isOpen={productFeedbackModal !== null}
                open={productFeedbackModal !== null}
                title="상품 피드백 등록"
                closable={true}
                onCancel={() => setProductFeedbackModal(null)}
                footer={
                  <>
                    <Button
                      type="primary"
                      onClick={() => {
                        onChangeProduct(
                          productFeedbackModal.product_id,
                          'apply_feedback',
                          productFeedback
                        );
                        setProductFeedbackModal(null);
                        setProductFeedback('');
                      }}
                    >
                      등록완료
                    </Button>
                  </>
                }
              >
                <Divider style={{ margin: '14px 0' }} />
                {productFeedbackModal && (
                  <div>
                    <p>이전 피드백: {productFeedbackModal.apply_feedback}</p>
                    <Input
                      placeholder="피드백을 입력해주세요"
                      value={productFeedback}
                      onChange={(e) => setProductFeedback(e.target.value)}
                    />
                  </div>
                )}
              </ModalLayout>
            </>
          ) : (
            <>
              <ModalLayout
                width={400}
                maxWidth={'calc(100% - 250px)'}
                isOpen={productFeedbackModal !== null}
                open={productFeedbackModal !== null}
                title="상품 피드백"
                closable={true}
                onCancel={() => setProductFeedbackModal(null)}
              >
                <Divider style={{ margin: '14px 0' }} />
                {productFeedbackModal && (
                  <div>
                    <p>{productFeedbackModal.apply_feedback}</p>
                  </div>
                )}
              </ModalLayout>
            </>
          )}
        </Spin>
      </Content>

      {/* 상품상세보기 */}
      <ModalLayout
        // type="drawer"
        centered
        // placement="bottom"
        title={'상품상세보기'}
        open={productModal !== null}
        onCancel={() => setProductModal(null)}
        closable
        width={'95%'}
        height={'95%'}
      >
        {productModal && (
          <ProductDetail
            permission={userInfo.type}
            defaultShopId={userInfo.shop_information_id}
            defaultProductId={productModal.product_id}
          />
        )}
      </ModalLayout>
    </>
  );
};

export default ProductAuditListPresenter;
